import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem, Alert, TextField, InputAdornment, Divider } from '@mui/material';
import {
    Search as SearchIcon,
    Menu as MenuIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Analytics as AnalyticsIcon,
    Explore as ExploreIcon,
    CallReceived as CallReceivedIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import LanguageToggle from '../../Components/Common/LanguageToggle.component';
import SearchDialog from './SearchDialog.component';

const Navbar = () => {
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState(null);
    const { toggleTheme, isDark } = useTheme();
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const { t } = useTranslation();

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSearchOpen = () => {
        setSearchDialogOpen(true);
    };

    const handleSearchClose = () => {
        setSearchDialogOpen(false);
    };

    const navigationItems = [
        { label: t('explorer.dashboard'), path: '/explorer', icon: <AnalyticsIcon /> },
        { label: t('explorer.explorer'), path: '/explorer/explore', icon: <ExploreIcon /> },
    ];

    return (
        <>
            {/* Beta Warning Banner */}
            <Alert
                severity="warning"
                sx={{
                    borderRadius: 0,
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderBottom: '1px solid rgba(255, 152, 0, 0.3)',
                    '& .MuiAlert-icon': { color: '#ff9800' }
                }}
            >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('explorer.betaWarning')}
                </Typography>
            </Alert>

            <AppBar position="sticky" sx={{ zIndex: 1201 }}>
                <Toolbar>
                    {/* Logo/Brand Name */}
                    <Typography
                        variant="h6"
                        component={Link}
                        to="/explorer"
                        sx={{
                            mr: { md: 4 }, // Removed margin for xs to allow more space
                            textDecoration: 'none',
                            color: 'inherit',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            flexShrink: 0,
                        }}
                    >
                        <AccountBalanceWalletIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Floki Scan <span style={{ fontWeight: 'bold' }}>by floki tools</span>
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Floki
                        </Box>
                    </Typography>

                    {/* Desktop Navigation Links */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                        {navigationItems.map((item) => (
                            <Button
                                key={item.path}
                                component={Link}
                                to={item.path}
                                color="inherit"
                                startIcon={item.icon}
                                sx={{
                                    backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </Box>

                    {/* FIX: Spacer to push all content to the right of it to the edge */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* FIX: Container for all DESKTOP right-side controls */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                        <Box sx={{ mx: 2, minWidth: 200, maxWidth: 300 }}>
                            <TextField
                                placeholder={t('explorer.searchPlaceholder')}
                                size="small"
                                onClick={handleSearchOpen}
                                InputProps={{
                                    readOnly: true,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                                        '& input': {
                                            cursor: 'pointer',
                                            '&::placeholder': { color: 'rgba(255, 255, 255, 0.7)', opacity: 1 },
                                        },
                                    },
                                }}
                                sx={{ width: '100%', cursor: 'pointer' }}
                            />
                        </Box>
                        <IconButton color="inherit" onClick={toggleTheme}>
                            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                        <LanguageToggle />
                        <Button
                            component={Link}
                            to="/"
                            color="inherit"
                            variant="outlined"
                            startIcon={<CallReceivedIcon />}
                            sx={{
                                ml: 1,
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                fontSize: { md: '0.75rem', lg: '0.875rem' },
                                px: { md: 1, lg: 2 },
                            }}
                        >
                            <Box sx={{ display: { md: 'none', lg: 'block' } }}>{t('explorer.mainSite')}</Box>
                            <Box sx={{ display: { md: 'block', lg: 'none' } }}>Main</Box>
                        </Button>
                    </Box>

                    {/* FIX: Mobile menu icon is now the ONLY item on the right on mobile */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            edge="end"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            keepMounted
                        >
                            {/* FIX: Moved all actions into the mobile menu */}
                            <MenuItem onClick={() => { handleSearchOpen(); handleMenuClose(); }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                                    <SearchIcon />
                                    {t('common.search')}
                                </Box>
                            </MenuItem>
                            <Divider sx={{ my: 1 }} />
                            {navigationItems.map((item) => (
                                <MenuItem
                                    key={item.path}
                                    component={Link}
                                    to={item.path}
                                    onClick={handleMenuClose}
                                    selected={location.pathname === item.path}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                                        {item.icon}
                                        {item.label}
                                    </Box>
                                </MenuItem>
                            ))}
                            <MenuItem component={Link} to="/" onClick={handleMenuClose}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                                    <CallReceivedIcon />
                                    {t('explorer.mainSite')}
                                </Box>
                            </MenuItem>
                            <Divider sx={{ my: 1 }} />
                            <MenuItem onClick={() => { toggleTheme(); handleMenuClose(); }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                                    {isDark ? <LightModeIcon /> : <DarkModeIcon />}
                                    {isDark ? t('common.lightMode') : t('common.darkMode')}
                                </Box>
                            </MenuItem>
                            {/* Clicks on the LanguageToggle will not close the menu */}
                            <MenuItem onClick={(e) => e.stopPropagation()} sx={{ justifyContent: 'center' }}>
                                <LanguageToggle />
                            </MenuItem>
                        </Menu>
                    </Box>

                    <SearchDialog open={searchDialogOpen} onClose={handleSearchClose} />
                </Toolbar>
            </AppBar>
        </>
    );
};

export default Navbar;